import React, { useState, useMemo } from "react";
import {
  Modal,
  Form,
  Select,
  Row,
  Col,
  Button,
  Card,
  List,
  Empty,
  Space,
  Typography,
  Spin,
  Divider,
  message,
  Radio,
  Tag,
} from "antd";
import {
  SearchOutlined,
  BankOutlined,
  UserOutlined,
  EditOutlined,
} from "@ant-design/icons";
import axios from "axios";
import { baseUrl, GET_COURT_AREA } from "../../../API/apiUrls";
import { allCourtOptions } from "../../../../utils/constant/Court";

const CourtSearchModal = ({
  visible,
  onCancel,
  onSelectCourt,
  geoRawData,
  searchAddress,
  customerIndex,
  customerName,
  allCustomers,
  editingCourt,
}) => {
  const [form] = Form.useForm();
  const [courtSearchResults, setCourtSearchResults] = useState([]);
  const [searchingCourt, setSearchingCourt] = useState(false);
  const [courtProvince, setCourtProvince] = useState(null);
  const [courtDistrict, setCourtDistrict] = useState(null);
  const [applyToAll, setApplyToAll] = useState(false);

  const isEditMode = !!editingCourt;

  // Set initial values เมื่อเปิด Modal
  React.useEffect(() => {
    if (visible && searchAddress) {
      form.setFieldsValue({
        province: searchAddress.PROVINCE,
        amphur: searchAddress.AMPHUR,
        tambon: searchAddress.TUMBON,
      });
      setCourtProvince(searchAddress.PROVINCE);
      setCourtDistrict(searchAddress.AMPHUR);

      if (editingCourt) {
        setApplyToAll(editingCourt.applyToAll || false);
      } else {
        setApplyToAll(false);
      }
    }
  }, [visible, searchAddress, editingCourt, form]);

  // ฟังก์ชันค้นหาศาล
  const handleCourtSearch = async () => {
    const values = form.getFieldsValue();

    setSearchingCourt(true);
    setCourtSearchResults([]);

    setTimeout(async () => {
      try {
        const cookieValue =
          "expapp=eyJ0b2tlbiI6ImV5SmhiR2NpT2lKSVV6STFOaUlzSW5SNWNDSTZJa3BYVkNKOS5leUpsZUhBaU9qRTNOakk0TkRVeE1USXNJbWxoZENJNk1UYzJNamcwTkRJeE1pd2lhWE56SWpvaWFIUjBjSE02THk5cGJXRjFkR2d1WW05eVlTNWtiM0JoTG1kdkxuUm9JaXdpWVhWMGFHOXlhWHBsWkNJNmRISjFaU3dpWVdOalpYTnpTVVFpT2lKaVl6ZGpNRE5rWXkwM1pERTJMVFF5TlRRdFlUSXhOeTAyWkRVNE5UYzRZak14TVRVaUxDSmhjSEJKUkNJNklqUXhNaklpTENKdmNtZEpSQ0k2SWpNMk1pSXNJbkpsWm5KbGMyaEpSQ0k2SW1ObFpHRTJZemc1TFRCalpEY3RORGhpTnkxaU5EaGlMVFZoWkRaallXTmpOVE5qTUNJc0luUjVjR1VpT2lKQlEwTkZVMU1pTENKTmIySnBiR1ZKUkNJNklqZzFZMlE0WXpReExUbGtZbUl0TkRjNE1TMDRaVEJrTFRRMU9HWm1ZekZqTUdRNFpDSXNJbk5qYjNCbElqb2ljR2xrSUdkcGRtVnVYMjVoYldVZ1ptRnRhV3g1WDI1aGJXVWlMQ0oyWlhKemFXOXVJam95ZlEuZkY4WGdfVThRUEtIVXEzX1o3eFdReGo4RjU1dTBQMENsMjVyYnNGTXQzVSIsImZhbWlseV9uYW1lIjoi4Lil4Li04LiC4Li04LiV4Lin4LiH4Lio4LmM4LiI4Li04LiZ4LiU4LiyIiwiZ2l2ZW5fbmFtZSI6IuC4meC4seC4kOC4p%2FC4uOC4kuC4tCJ9.smCw%2BVS6X4ygJ27mKHdRP04ADajc7KOxSLVhCNDzf6c";

        const params = {
          cookie: cookieValue,
        };

        if (values.courtRunning) params.court_running = values.courtRunning;
        if (values.province) params.prov_id = values.province;
        if (values.amphur) params.amphur_id = values.amphur;
        if (values.tambon) params.tambon_id = values.tambon + "00";

        console.log("🔍 Searching court with params:", params);

        const res = await axios.get(baseUrl + GET_COURT_AREA, { params });

        console.log("✅ Court search results:", res.data);
        setCourtSearchResults(res.data || []);

        if (!res.data || res.data.length === 0) {
          message.info("ไม่พบศาลตามเงื่อนไขที่ค้นหา");
        } else {
          message.success(`พบศาล ${res.data.length} แห่ง`);
        }
      } catch (error) {
        console.error("❌ Error loading court:", error);
        message.error(`ไม่สามารถค้นหาศาลได้: ${error.message}`);
        setCourtSearchResults([]);
      } finally {
        setSearchingCourt(false);
      }
    }, 2000);
  };

  // เลือกศาล
  const handleSelectCourt = (court) => {
    onSelectCourt({
      court,
      customerIndex,
      applyToAll,
    });
  };

  // จังหวัดทั้งหมด
  const courtProvinceOptions = useMemo(() => {
    if (!geoRawData?.province) return [];
    return geoRawData.province.map((prov) => ({
      label: prov.provDesc,
      value: prov.provCode,
    }));
  }, [geoRawData]);

  // ✅ อำเภอ
  const courtDistrictOptions = useMemo(() => {
    if (!geoRawData?.district || !courtProvince) return [];

    const filteredDistricts = geoRawData.district.filter(
      (dist) => dist.provCode === courtProvince.toString()
    );

    return filteredDistricts.map((dist, index) => ({
      label: dist.distDesc,
      value: dist.distCode,
      data: dist,
      key: `court-dist-${dist.provCode}-${dist.distCode}-${index}`,
    }));
  }, [geoRawData, courtProvince]);

  // ✅ ตำบล
  const courtSubDistrictOptions = useMemo(() => {
    if (!geoRawData?.subDistrict || !courtDistrict) return [];

    const filteredSubDistricts = geoRawData.subDistrict.filter(
      (sub) =>
        sub.provCode === courtProvince.toString() &&
        sub.distCode === courtDistrict
    );

    return filteredSubDistricts.map((sub, index) => ({
      label: sub.subDistDesc,
      value: sub.subDistCode,
      data: sub,
      key: `court-sub-${sub.provCode}-${sub.distCode}-${sub.subDistCode}-${index}`,
    }));
  }, [geoRawData, courtProvince, courtDistrict]);

  // ✅ Handler: เลือกชื่อศาล - Reset ทุกอย่าง
  const handleCourtChange = (value) => {
    setCourtProvince(null);
    setCourtDistrict(null);
    form.setFieldsValue({
      province: undefined,
      amphur: undefined,
      tambon: undefined,
    });
  };

  // ✅ Handler: เลือกจังหวัด - Reset อำเภอและตำบล
  const handleCourtProvinceChange = (value) => {
    if (!value) {
      // Clear จังหวัด
      setCourtProvince(null);
      setCourtDistrict(null);
      form.setFieldsValue({
        amphur: undefined,
        tambon: undefined,
      });
    } else {
      // เลือกจังหวัดใหม่
      setCourtProvince(value);
      setCourtDistrict(null);
      form.setFieldsValue({
        amphur: undefined,
        tambon: undefined,
      });
    }
  };

  // ✅ Handler: เลือกอำเภอ - Reset ตำบล
  const handleCourtDistrictChange = (value) => {
    if (!value) {
      // Clear อำเภอ
      setCourtDistrict(null);
      form.setFieldsValue({
        tambon: undefined,
      });
    } else {
      // เลือกอำเภอใหม่
      setCourtDistrict(value);
      form.setFieldsValue({
        tambon: undefined,
      });
    }
  };

  // ✅ Handler: เลือกตำบล - ไม่ต้องทำอะไร
  const handleCourtSubDistrictChange = (value) => {
    // ไม่ต้อง auto-fill อะไร เพราะบังคับให้เลือกตามลำดับแล้ว
  };

  const handleCancel = () => {
    form.resetFields();
    setCourtSearchResults([]);
    setCourtProvince(null);
    setCourtDistrict(null);
    setApplyToAll(false);
    onCancel();
  };

  return (
    <Modal
      title={
        <Space>
          {isEditMode ? (
            <EditOutlined style={{ fontSize: 20, color: "#ff9800" }} />
          ) : (
            <BankOutlined style={{ fontSize: 20 }} />
          )}
          <span style={{ fontSize: 18 }}>
            {isEditMode ? "แก้ไขศาล" : "เลือกศาล"}
          </span>
        </Space>
      }
      open={visible}
      onCancel={handleCancel}
      footer={null}
      width={950}
    >
      {/* ✅ แสดงข้อมูลศาลเดิมถ้าเป็นโหมดแก้ไข */}
      {isEditMode && editingCourt && (
        <Card
          size="small"
          style={{ marginBottom: 16, background: "#fff7e6" }}
          styles={{
            body: { padding: "12px 16px" },
          }}
        >
          <Space direction="vertical" size={8} style={{ width: "100%" }}>
            <Typography.Text strong style={{ fontSize: 15 }}>
              <EditOutlined /> กำลังแก้ไขศาล:
            </Typography.Text>
            <Typography.Text style={{ fontSize: 14 }}>
              {editingCourt.courtData.court_name}
            </Typography.Text>
            <Tag color="blue" style={{ fontSize: 12 }}>
              ค่าส่งหมาย: {editingCourt.courtData.amtsentnotice} บาท
            </Tag>
          </Space>
        </Card>
      )}

      {/* แสดงชื่อ Customer ที่กำลังเพิ่ม/แก้ไขศาล */}
      <Card
        size="small"
        style={{ marginBottom: 16, background: "#e6f7ff" }}
        styles={{
          body: { padding: "12px 16px" },
        }}
      >
        <Space>
          <UserOutlined style={{ fontSize: 18, color: "#1890ff" }} />
          <Typography.Text strong style={{ fontSize: 15 }}>
            {isEditMode ? "แก้ไข" : "เพิ่ม"}ศาลสำหรับ: {customerName}
          </Typography.Text>
        </Space>
      </Card>

      <Card
        size="small"
        style={{ marginBottom: 20 }}
        styles={{
          body: {
            padding: "20px",
          },
        }}
      >
        <Form form={form} layout="vertical">
          <Row gutter={16}>
            <Col span={24}>
              <Form.Item
                name="courtRunning"
                label={
                  <span style={{ fontSize: 14, fontWeight: 500 }}>ชื่อศาล</span>
                }
              >
                <Select
                  showSearch
                  placeholder="เลือกศาล (ไม่บังคับ)"
                  optionFilterProp="label"
                  options={allCourtOptions}
                  onChange={handleCourtChange}
                  allowClear
                  size="large"
                  style={{ fontSize: 14 }}
                />
              </Form.Item>
            </Col>
          </Row>

          <Divider style={{ margin: "16px 0" }}>หรือค้นหาตามพื้นที่</Divider>

          <Row gutter={16}>
            <Col span={8}>
              <Form.Item
                name="province"
                label={
                  <span style={{ fontSize: 14, fontWeight: 500 }}>จังหวัด</span>
                }
              >
                <Select
                  showSearch
                  placeholder="เลือกจังหวัด (ไม่บังคับ)"
                  optionFilterProp="label"
                  options={courtProvinceOptions}
                  onChange={handleCourtProvinceChange}
                  allowClear
                  loading={!geoRawData}
                  size="large"
                />
              </Form.Item>
            </Col>

            <Col span={8}>
              <Form.Item
                name="amphur"
                label={
                  <span style={{ fontSize: 14, fontWeight: 500 }}>
                    อำเภอ/เขต
                  </span>
                }
              >
                <Select
                  showSearch
                  placeholder={
                    courtProvince
                      ? "เลือกอำเภอ (ไม่บังคับ)"
                      : "กรุณาเลือกจังหวัดก่อน"
                  }
                  optionFilterProp="label"
                  options={courtDistrictOptions}
                  onChange={handleCourtDistrictChange}
                  allowClear
                  size="large"
                  disabled={!courtProvince}
                />
              </Form.Item>
            </Col>

            <Col span={8}>
              <Form.Item
                name="tambon"
                label={
                  <span style={{ fontSize: 14, fontWeight: 500 }}>
                    ตำบล/แขวง
                  </span>
                }
              >
                <Select
                  showSearch
                  placeholder={
                    courtDistrict
                      ? "เลือกตำบล (ไม่บังคับ)"
                      : "กรุณาเลือกอำเภอก่อน"
                  }
                  optionFilterProp="label"
                  options={courtSubDistrictOptions}
                  onChange={handleCourtSubDistrictChange}
                  allowClear
                  size="large"
                  disabled={!courtDistrict}
                />
              </Form.Item>
            </Col>
          </Row>

          <Row>
            <Col span={24} style={{ textAlign: "right", marginTop: 8 }}>
              <Button
                type="primary"
                size="large"
                icon={<SearchOutlined />}
                onClick={handleCourtSearch}
                loading={searchingCourt}
                style={{ fontSize: 15 }}
              >
                ค้นหาศาล
              </Button>
            </Col>
          </Row>

          <Typography.Text
            type="secondary"
            style={{ fontSize: 13, display: "block", marginTop: 12 }}
          >
            <SearchOutlined /> ระบบจะค้นหาหลังจากกดปุ่ม "ค้นหาศาล" (หน่วงเวลา 2
            วินาที)
          </Typography.Text>
        </Form>
      </Card>

      {/* ตัวเลือกการใช้ศาลกับ Customer */}
      {allCustomers && allCustomers.length > 1 && (
        <Card
          size="small"
          style={{ marginBottom: 20, background: "#fffbe6" }}
          styles={{
            body: { padding: "16px" },
          }}
        >
          <Typography.Text
            strong
            style={{ fontSize: 14, display: "block", marginBottom: 12 }}
          >
            นำไปใช้กับ:
          </Typography.Text>
          <Radio.Group
            value={applyToAll}
            onChange={(e) => setApplyToAll(e.target.value)}
            style={{ width: "100%" }}
          >
            <Space direction="vertical" style={{ width: "100%" }}>
              <Radio value={false} style={{ fontSize: 14 }}>
                <strong>{customerName}</strong> (เท่านั้น)
              </Radio>
              <Radio value={true} style={{ fontSize: 14 }}>
                <strong>ทุกคนในสัญญา</strong> (ผู้กู้ + ผู้ค้ำทั้งหมด{" "}
                {allCustomers.length} คน)
              </Radio>
            </Space>
          </Radio.Group>
        </Card>
      )}

      <Card
        title={<span style={{ fontSize: 16 }}>ผลการค้นหาศาล</span>}
        size="small"
        styles={{
          header: {
            background: "#f0f5ff",
            borderBottom: "2px solid #adc6ff",
            padding: "12px 16px",
          },
          body: {
            padding: "16px",
          },
        }}
      >
        {searchingCourt ? (
          <div style={{ textAlign: "center", padding: "50px 0" }}>
            <Spin size="large" />
            <Typography.Text
              type="secondary"
              style={{ display: "block", marginTop: 20, fontSize: 15 }}
            >
              กำลังค้นหาศาล...
            </Typography.Text>
          </div>
        ) : courtSearchResults.length > 0 ? (
          <List
            dataSource={courtSearchResults}
            renderItem={(court) => (
              <List.Item
                actions={[
                  <Button
                    type="primary"
                    size="middle"
                    onClick={() => handleSelectCourt(court)}
                    style={{ fontSize: 14 }}
                  >
                    {isEditMode ? "เลือกศาลนี้" : "เลือก"}
                  </Button>,
                ]}
                style={{ padding: "16px" }}
              >
                <List.Item.Meta
                  avatar={
                    <BankOutlined style={{ fontSize: 28, color: "#1890ff" }} />
                  }
                  title={
                    <span style={{ fontSize: 16, fontWeight: 600 }}>
                      {court.court_name}
                    </span>
                  }
                  description={
                    <Space
                      direction="vertical"
                      size={4}
                      style={{ marginTop: 8 }}
                    >
                      <Typography.Text style={{ fontSize: 14 }}>
                        <strong>พื้นที่:</strong> จังหวัด: {court.prov_name} |
                        อำเภอ: {court.amphur_name} | ตำบล: {court.tambon_name}
                      </Typography.Text>
                      {court.amtsentnotice && (
                        <Typography.Text style={{ fontSize: 14 }}>
                          <strong>ค่าส่งหมาย:</strong> {court.amtsentnotice} บาท
                        </Typography.Text>
                      )}
                      {court.remark && (
                        <Typography.Text
                          style={{ fontSize: 13, color: "#666" }}
                        >
                          <strong>หมายเหตุ:</strong> {court.remark}
                        </Typography.Text>
                      )}
                    </Space>
                  }
                />
              </List.Item>
            )}
            bordered
            style={{ maxHeight: 450, overflowY: "auto" }}
          />
        ) : (
          <Empty
            description={
              <span style={{ fontSize: 14 }}>
                ไม่พบข้อมูลศาล กรุณากดปุ่ม "ค้นหาศาล"
              </span>
            }
            image={Empty.PRESENTED_IMAGE_SIMPLE}
          />
        )}
      </Card>
    </Modal>
  );
};

export default CourtSearchModal;
